import type { AudioLevel } from "./audio.js";

type CaptionHandler = (
  text: string,
  final: boolean,
  sequence: number,
) => void;

type StateHandler = (state: string, enabled: boolean) => void;

type RecorderFactory = (
  stream: MediaStream,
  mimeType: string,
) => MediaRecorder;

type MobileSpeechOptions = {
  fetchImpl?: typeof fetch;
  recorderFactory?: RecorderFactory;
  minimumSegmentMs?: number;
  maximumSegmentMs?: number;
  silenceMs?: number;
  speechThresholdDbfs?: number;
};

const MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
  "audio/ogg;codecs=opus",
] as const;

const defaultRecorderFactory: RecorderFactory = (stream, mimeType) => {
  try {
    return new MediaRecorder(stream, {
      mimeType,
      audioBitsPerSecond: 32_000,
    });
  } catch {
    return new MediaRecorder(stream, { mimeType });
  }
};

const responseError = async (response: Response): Promise<string> => {
  try {
    const payload = (await response.json()) as { error?: unknown };
    if (typeof payload.error === "string" && payload.error.trim()) {
      return payload.error;
    }
  } catch {
    // JSON 오류 본문이 아니면 상태 코드만 사용합니다.
  }
  return `모바일 자막 요청 실패 (${response.status})`;
};

export class MobileServerSpeechController {
  private readonly fetchImpl: typeof fetch;
  private readonly recorderFactory: RecorderFactory;
  private readonly minimumSegmentMs: number;
  private readonly maximumSegmentMs: number;
  private readonly silenceMs: number;
  private readonly speechThresholdDbfs: number;
  private recorder: MediaRecorder | undefined;
  private mimeType = "";
  private chunks: Blob[] = [];
  private enabled = false;
  private inputActive = true;
  private playbackActive = false;
  private segmentStartedAt = 0;
  private lastVoiceAt = 0;
  private segmentHasSpeech = false;
  private uploadStoppedSegment = false;
  private maximumSegmentTimer = 0;
  private silenceTimer = 0;
  private sequence = 0;
  private generation = 0;
  private queuedUploads = 0;
  private uploadChain: Promise<void> = Promise.resolve();
  private readonly uploadAbortControllers = new Set<AbortController>();

  constructor(
    private readonly stream: MediaStream,
    private readonly roomId: string,
    private readonly token: string,
    private readonly onCaption: CaptionHandler,
    private readonly onState: StateHandler,
    options: MobileSpeechOptions = {},
  ) {
    this.fetchImpl =
      options.fetchImpl ?? ((input, init) => window.fetch(input, init));
    this.recorderFactory = options.recorderFactory ?? defaultRecorderFactory;
    this.minimumSegmentMs = options.minimumSegmentMs ?? 2_500;
    this.maximumSegmentMs = options.maximumSegmentMs ?? 8_000;
    this.silenceMs = options.silenceMs ?? 850;
    this.speechThresholdDbfs = options.speechThresholdDbfs ?? -50;
  }

  enable(): Promise<boolean> {
    if (this.enabled) return Promise.resolve(true);
    const track = this.stream.getAudioTracks()[0];
    if (!track || track.readyState !== "live" || typeof MediaRecorder === "undefined") {
      this.onState("이 기기에서는 모바일 AI 자막을 사용할 수 없습니다", false);
      return Promise.resolve(false);
    }
    this.mimeType = this.selectMimeType();
    if (!this.mimeType) {
      this.onState("지원되는 모바일 자막 오디오 형식이 없습니다", false);
      return Promise.resolve(false);
    }
    this.enabled = true;
    this.generation += 1;
    this.syncRecordingActivity();
    return Promise.resolve(true);
  }

  disable(): void {
    this.enabled = false;
    this.generation += 1;
    this.stopSegment(false);
    for (const controller of this.uploadAbortControllers) controller.abort();
    this.uploadAbortControllers.clear();
    this.onState("모바일 AI 자막 꺼짐", false);
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  setInputActive(active: boolean): void {
    if (this.inputActive === active) return;
    this.inputActive = active;
    this.syncRecordingActivity();
  }

  setPlaybackActive(active: boolean): void {
    if (this.playbackActive === active) return;
    this.playbackActive = active;
    this.syncRecordingActivity();
  }

  setAudioLevel(level: AudioLevel): void {
    if (!this.recorder || this.recorder.state !== "recording") return;
    if (level.dbfs > this.speechThresholdDbfs) {
      this.segmentHasSpeech = true;
      const now = performance.now();
      this.lastVoiceAt = now;
      // 마이크 레벨 샘플은 화면 갱신 주기로 계속 들어오므로, 브라우저가
      // setTimeout/MediaRecorder timeslice를 늦추는 경우에도 최대 길이를 지킵니다.
      if (now - this.segmentStartedAt >= this.maximumSegmentMs) {
        this.stopSegment(true);
        return;
      }
      window.clearTimeout(this.silenceTimer);
      const minimumRemaining = Math.max(
        0,
        this.minimumSegmentMs - (now - this.segmentStartedAt),
      );
      this.silenceTimer = window.setTimeout(
        () => this.stopSegment(true),
        Math.max(this.silenceMs, minimumRemaining),
      );
    }
  }

  private isSuspended(): boolean {
    return !this.inputActive || this.playbackActive;
  }

  private syncRecordingActivity(): void {
    if (!this.enabled) return;
    if (this.isSuspended()) {
      this.stopSegment(false);
      this.onState(
        this.playbackActive
          ? "텍스트 음성 재생 중 · 모바일 자막 일시 중지"
          : "마이크 꺼짐 · 모바일 자막 일시 중지",
        true,
      );
      return;
    }
    this.startSegment();
    if (this.enabled && this.recorder) {
      this.onState("모바일 OpenAI 자막 켜짐", true);
    }
  }

  private selectMimeType(): string {
    return (
      MIME_TYPES.find((candidate) => MediaRecorder.isTypeSupported(candidate)) ??
      ""
    );
  }

  private startSegment(): void {
    if (
      !this.enabled ||
      this.isSuspended() ||
      this.recorder
    ) {
      return;
    }

    let recorder: MediaRecorder;
    try {
      const audioTrack = this.stream.getAudioTracks()[0];
      if (!audioTrack || audioTrack.readyState !== "live") {
        throw new Error("모바일 자막 마이크 트랙이 종료되었습니다");
      }
      recorder = this.recorderFactory(
        new MediaStream([audioTrack]),
        this.mimeType,
      );
    } catch {
      this.enabled = false;
      this.onState("모바일 자막 녹음을 시작하지 못했습니다", false);
      return;
    }

    this.recorder = recorder;
    this.chunks = [];
    this.segmentHasSpeech = false;
    this.uploadStoppedSegment = false;
    this.segmentStartedAt = performance.now();
    this.lastVoiceAt = this.segmentStartedAt;
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) this.chunks.push(event.data);
      this.rotateSegmentWhenReady(recorder);
    };
    recorder.onerror = () => {
      if (!this.enabled) return;
      this.onState("모바일 자막 오디오 처리 오류 · 다시 연결 중", true);
    };
    recorder.onstop = () => this.onSegmentStopped(recorder);

    try {
      // Android 계열 브라우저에서는 백그라운드 타이머가 지연될 수 있으므로
      // MediaRecorder 자체의 주기 이벤트도 구간 종료 시계로 사용합니다.
      recorder.start(1_000);
    } catch {
      this.recorder = undefined;
      this.enabled = false;
      this.onState("모바일 자막 녹음을 시작하지 못했습니다", false);
      return;
    }
    window.clearTimeout(this.maximumSegmentTimer);
    this.maximumSegmentTimer = window.setTimeout(
      () => this.stopSegment(this.segmentHasSpeech),
      this.maximumSegmentMs,
    );
  }

  private rotateSegmentWhenReady(recorder: MediaRecorder): void {
    if (recorder !== this.recorder || recorder.state !== "recording") return;
    const now = performance.now();
    const durationMs = now - this.segmentStartedAt;
    if (durationMs >= this.maximumSegmentMs) {
      this.stopSegment(this.segmentHasSpeech);
      return;
    }
    if (
      this.segmentHasSpeech &&
      durationMs >= this.minimumSegmentMs &&
      now - this.lastVoiceAt >= this.silenceMs
    ) {
      this.stopSegment(true);
    }
  }

  private stopSegment(upload: boolean): void {
    window.clearTimeout(this.maximumSegmentTimer);
    window.clearTimeout(this.silenceTimer);
    this.maximumSegmentTimer = 0;
    this.silenceTimer = 0;
    const recorder = this.recorder;
    if (!recorder || recorder.state === "inactive") return;
    this.uploadStoppedSegment = upload;
    try {
      recorder.stop();
    } catch {
      this.recorder = undefined;
    }
  }

  private onSegmentStopped(recorder: MediaRecorder): void {
    if (recorder !== this.recorder) return;
    window.clearTimeout(this.maximumSegmentTimer);
    window.clearTimeout(this.silenceTimer);
    this.maximumSegmentTimer = 0;
    this.silenceTimer = 0;
    const durationMs = Math.max(0, performance.now() - this.segmentStartedAt);
    const shouldUpload = this.uploadStoppedSegment;
    const blob = new Blob(this.chunks, { type: this.mimeType });
    this.recorder = undefined;
    this.chunks = [];
    this.uploadStoppedSegment = false;

    if (this.enabled && !this.isSuspended()) this.startSegment();
    if (shouldUpload && blob.size >= 256) {
      this.queueUpload(blob, durationMs);
    }
  }

  private queueUpload(blob: Blob, durationMs: number): void {
    if (this.queuedUploads >= 3) {
      this.onState("모바일 자막 서버 지연 · 일부 발언 구간 생략", true);
      return;
    }
    const sequence = this.sequence;
    const generation = this.generation;
    this.sequence += 1;
    this.queuedUploads += 1;
    const upload = this.uploadChain.then(() => {
      if (!this.enabled || generation !== this.generation) return;
      return this.uploadSegment(blob, durationMs, sequence);
    });
    this.uploadChain = upload
      .catch(() => undefined)
      .finally(() => {
        this.queuedUploads -= 1;
      });
  }

  private async uploadSegment(
    blob: Blob,
    durationMs: number,
    sequence: number,
  ): Promise<void> {
    const controller = new AbortController();
    this.uploadAbortControllers.add(controller);
    const timeout = window.setTimeout(() => controller.abort(), 35_000);
    try {
      const response = await this.fetchImpl(
        `/api/rooms/${encodeURIComponent(this.roomId)}/transcriptions`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.token}`,
            "Content-Type": this.mimeType,
            "X-Caption-Sequence": String(sequence),
            "X-Audio-Duration-Ms": String(Math.round(durationMs)),
          },
          body: blob,
          signal: controller.signal,
        },
      );
      if (!response.ok) {
        const message = await responseError(response);
        if ([401, 403, 404, 415, 503].includes(response.status)) {
          this.enabled = false;
          this.stopSegment(false);
          this.onState(message, false);
          return;
        }
        throw new Error(message);
      }
      const payload = (await response.json()) as { text?: unknown };
      const text = typeof payload.text === "string" ? payload.text.trim() : "";
      if (text) this.onCaption(text, true, sequence);
      if (this.enabled && !this.isSuspended()) {
        this.onState("모바일 OpenAI 자막 켜짐", true);
      }
    } catch (error) {
      if (controller.signal.aborted || !this.enabled) return;
      this.onState(
        `${error instanceof Error ? error.message : "모바일 자막 서버 오류"} · 다시 시도 중`,
        true,
      );
    } finally {
      window.clearTimeout(timeout);
      this.uploadAbortControllers.delete(controller);
    }
  }
}
